import * as React from 'react'
import * as R from 'ramda'
import * as ThemeUI from 'theme-ui'
import * as ReactRouter from 'react-router-dom'
import PropTypes from 'prop-types'

import * as Herz from '@rushplay/herz'
import * as Forms from '@rushplay/forms'

import * as Constants from '../constants'
import * as Cookies from '../cookies'
import * as Suspense from '../suspense'
import { FieldBuilder } from '../field-builder'
import { InputField } from '../input-field'
import { Link } from '../link'
import { SubmitButton } from '../submit-button'
import { useSafeUpdateQuery } from '../use-safe-update-query'

import { Drawer } from './drawer'
import { uiSchema } from './login-drawer-ui-schema'

const schema = {
  type: 'object',
  properties: {
    email: {
      type: 'string',
      pattern: Constants.EmailPattern,
    },
    password: {
      type: 'string',
      pattern: Constants.PasswordPattern,
    },
  },
  required: R.filter(Boolean, ['email', 'password']),
}

export function InputFieldPassword(props) {
  return <InputField {...props} type="password" />
}

export function LoginDrawer(props) {
  const translate = Herz.I18n.useTranslate()

  return (
    <>
      {props.isLandingPage ? (
        <ThemeUI.Box
          sx={{
            textShadow: '0px 1px 8px #081C26',
            fontSize: '34px',
            fontWeight: 700,
            lineHeight: '54px',
          }}
        >
          {translate('landing-page.login-drawer.title')}
        </ThemeUI.Box>
      ) : (
        <Drawer.Title>{translate('login-drawer.title')}</Drawer.Title>
      )}

      <Drawer.Content>
        <Suspense.Boundary>
          <Form />
        </Suspense.Boundary>
      </Drawer.Content>
    </>
  )
}

LoginDrawer.propTypes = {
  isLandingPage: PropTypes.bool,
}

Herz.I18n.Loader.preload(
  ['login-drawer.title', 'landing-page.login-drawer.title'],
  LoginDrawer
)

const i18nKeys = {
  errorAuthFailed: 'login.authorization-failed-error',
  errorLockedSeon: 'errors.login.seon-lock',
  errorLockedTimeout: 'errors.login.timeout-lock',
  forgotPassword: 'login-drawer.forgot-password',
  noAccountTitle: 'login-drawer.no-account.title',
  noAccountLink: 'login-drawer.no-account.link',
}

function Form() {
  const [error, setError] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [, setAuthToken] = Cookies.useCookie(Constants.Cookies.TOKEN)
  const translate = Herz.I18n.useTranslate()
  const session = Herz.Auth.useSession()
  const history = ReactRouter.useHistory()
  const registerQuery = useSafeUpdateQuery({ register: 'me' })
  const forgotPasswordQuery = useSafeUpdateQuery({ 'forgot-password': 'me' })

  return (
    <Forms.Provider
      name="login"
      schema={schema}
      onSubmit={(formErrors, data) => {
        setLoading(true)
        setError(null)
        if (R.isEmpty(formErrors)) {
          session
            .create({
              username: data.email,
              password: data.password,
            })
            .then((response) => {
              if (response.error) {
                setLoading(false)
                throw response.error
              }

              setAuthToken(
                response?.createSession.token,
                Constants.CookieOptions[Constants.Cookies.TOKEN]
              )

              history.replace(history.location.pathname)
            })
            // TODO: Implement error handling using notifications
            .catch((error) => {
              setAuthToken(null, {
                path: '/',
              })
              setLoading(false)
              // TODO: Do not use strings; match with instanceof
              // Instances of different classes could still provide same string
              // as their name thus this is not a reliable way to match errors
              // See: https://github.com/RushPlay/frontend-platform/issues/36
              switch (error.name) {
                case 'LockedError': {
                  if (error.reason === 'seon') {
                    return setError(i18nKeys.errorLockedSeon)
                  }
                  if (error.reason === 'timeout') {
                    return setError(i18nKeys.errorLockedTimeout)
                  }
                  return setError(i18nKeys.errorAuthFailed)
                }

                default: {
                  return setError(i18nKeys.errorAuthFailed)
                }
              }
            })
        }
      }}
    >
      <ThemeUI.Flex
        sx={{
          flexDirection: 'column',
        }}
      >
        <FieldBuilder
          getCustomField={(scope) => {
            if (scope === '#/properties/password') {
              return InputFieldPassword
            }
          }}
          uiSchema={uiSchema}
        />

        {error ? (
          <ThemeUI.Box sx={{ mt: 1 }}>
            <ThemeUI.Text variant="error">{translate(error)}</ThemeUI.Text>
          </ThemeUI.Box>
        ) : null}

        <Link
          sx={{
            mt: 0,
            display: 'inline-block',
            ml: 'auto',
            fontSize: 'xs',
            lineHeight: '16px',
            color: 'faded-white',
          }}
          to={`?${forgotPasswordQuery}`}
        >
          {translate(i18nKeys.forgotPassword)}
        </Link>

        <SubmitButton
          loading={loading}
          sx={{
            mt: ['23px', '18px'],
            py: '12px',
          }}
        />

        <ThemeUI.Box
          sx={{
            textAlign: 'center',
            mt: ['19px', '7px'],
            lineHeight: '16px',
            color: 'faded-white',
            fontSize: 'xs',
            fontWeight: 400,
          }}
        >
          {translate(i18nKeys.noAccountTitle)}{' '}
          <ThemeUI.Link
            as={ReactRouter.Link}
            to={`?${registerQuery}`}
            sx={{ fontSize: 'xs' }}
          >
            {translate(i18nKeys.noAccountLink)}
          </ThemeUI.Link>
        </ThemeUI.Box>
      </ThemeUI.Flex>
    </Forms.Provider>
  )
}

Herz.I18n.Loader.preload(Object.values(i18nKeys), Form)

// for @loadable/components
export default LoginDrawer
